import { SparklesIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import type { Persona } from "../../hooks/useSandboxPersonas";
import type { SandboxEntityType } from "../../types/entities";
import type { Interaction, InteractionType } from "../../types/interactions";
import { InfluenceBreakdown } from "./InfluenceBreakdown";
import { InteractionContributions } from "./InteractionContributions";
import { NearestEntities } from "./NearestEntities";

interface EmbeddingInspectorProps {
  interactions: Interaction[] | null;
  userEmbedding: number[] | null;
  personas: Persona[];
  currentPersonaId?: string;
  getWeight: (
    entityType: SandboxEntityType,
    interactionType: InteractionType,
  ) => number;
  getDecayMultiplier: (interaction: Interaction) => number;
  description: string | null;
  isLoadingDescription: boolean;
  onDescribe: () => void;
}

const EmbeddingInspector = (props: EmbeddingInspectorProps) => {
  const {
    interactions,
    userEmbedding,
    getWeight,
    getDecayMultiplier,
    description,
    isLoadingDescription,
    onDescribe,
  } = props;

  if (!userEmbedding || userEmbedding.length === 0 || !interactions) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-border">
        <p className="text-sm text-muted-foreground">
          Run analysis to inspect the breakdown
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InfluenceBreakdown
        interactions={interactions}
        getWeight={getWeight}
        getDecayMultiplier={getDecayMultiplier}
      />

      <div className="space-y-6 overflow-y-auto max-h-72 pr-1">
        <InteractionContributions
          interactions={interactions}
          getWeight={getWeight}
          getDecayMultiplier={getDecayMultiplier}
        />

        <NearestEntities
          userEmbedding={userEmbedding}
          interactions={interactions}
        />
      </div>

      <section className="space-y-3 pt-2 border-t border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Persona description</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={onDescribe}
            disabled={isLoadingDescription}
          >
            {isLoadingDescription ? (
              <Spinner className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <SparklesIcon className="mr-1.5 h-3.5 w-3.5" />
            )}
            {isLoadingDescription ? "Describing…" : "Describe persona"}
          </Button>
        </div>
        {description && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {description}
          </p>
        )}
      </section>
    </div>
  );
};

export default EmbeddingInspector;
